/**
 * Turning one provider collection into records for the interval on screen.
 *
 * This is where the two modes are decided. A range narrow enough to read
 * becomes a list of items; anything wider becomes one count per bucket, because
 * a year of mail is a number, not four hundred rows. Only this module knows the
 * target range, so only it can make that call -- the provider module is handed
 * a decided [from, to] and asked for one thing or the other.
 */

import { constructInterval, countN, shiftN } from "@/common/intervals";
import { getChildType } from "@/common/intervalTypes";
import { HGInterval, HGTimeRecordPlus } from "@/common/types";
import { ordinalToDate } from "@/common/unixTime";
import { IntervalsRange, MapInterval } from "@/engine/engine";

import { adaptItems, adaptSummary } from "./adapt";
import { getProvider } from "./registry";
import { getAccessToken } from "./tokens";
import {
  ExternalAuthError,
  ExternalProviderModule,
  ProviderConfig,
  ProviderFetchContext,
} from "./types";

const MS_PER_DAY = 86_400_000;
/**
 * Above this many buckets the level is summarised as a whole instead. A century
 * of decades is ten requests; a century of years would be a hundred, for ten
 * pixels of number each.
 */
const SUMMARY_FAN_LIMIT = 24;

export interface ExternalSource {
  collectionId: string;
  providerKey: string;
  config: ProviderConfig;
  connectionId: string | null;
}

export type ExternalSourceState =
  | "ok"
  /** No grant yet, or the grant is gone: the UI offers to connect. */
  | "auth"
  | "error"
  /** Nothing this provider covers falls inside the visible range. */
  | "empty"
  /** The provider key names a module this build does not have. */
  | "unsupported";

export interface ExternalSourceStatus {
  collectionId: string;
  providerKey: string;
  title: string;
  state: ExternalSourceState;
  message?: string;
  /** The item cap was reached and the list is only the first page of reality. */
  truncated?: boolean;
}

export interface ExternalLoadResult {
  records: HGTimeRecordPlus[];
  status: ExternalSourceStatus;
}

export interface ExternalLoadTarget {
  interval: HGInterval;
  endInterval: HGInterval | null;
  secondsRange: [bigint, bigint];
  userId: string;
}

/** A stable key over the sources, so the loader's cache notices a config edit. */
export function externalSourcesKey(sources: ExternalSource[]): string {
  if (sources.length === 0) return "";
  return sources
    .map(
      (source) =>
        `${source.collectionId}:${source.providerKey}:${source.connectionId ?? ""}:${JSON.stringify(source.config)}`,
    )
    .sort()
    .join("|");
}

/** Clamp the visible range to what the provider could possibly know about. */
function windowFor(
  provider: ExternalProviderModule,
  secondsRange: [bigint, bigint],
): [Date, Date] | null {
  const from = ordinalToDate(secondsRange[0]);
  const to = ordinalToDate(secondsRange[1]);
  // Both null means the whole range predates 1970 -- a Gmail collection viewed
  // at the Jurassic, which costs no network at all.
  if (to === null) return null;

  const earliest = provider.limits.earliest ?? new Date(0);
  const start = from && from > earliest ? from : earliest;
  if (start >= to) return null;
  return [start, to];
}

/**
 * The buckets a summarised level is broken into.
 *
 * Derived from the interval type's own child type rather than from the
 * timeline's groupBy option: the loader runs before the tree is built and must
 * not depend on a view setting the user can change underneath it.
 */
function summaryBuckets(
  interval: HGInterval,
  endInterval: HGInterval | null,
): HGInterval[] {
  const childIt = getChildType(interval.it);
  if (!childIt) return [interval];

  const [firstN, lastN] = IntervalsRange(interval, endInterval, childIt);
  const count = countN(firstN, lastN);
  if (count <= 0) return [interval];
  if (count > SUMMARY_FAN_LIMIT) return [interval];

  const buckets: HGInterval[] = [];
  let n = firstN;
  for (let i = 0; i < count; i++) {
    buckets.push(constructInterval(n, childIt));
    n = shiftN(n, 1n);
  }
  return buckets;
}

function bucketWindow(
  bucket: HGInterval,
  provider: ExternalProviderModule,
  now: Date,
): [Date, Date] | null {
  const span = MapInterval(bucket, "s");
  const from = ordinalToDate(span.begin.n);
  const to = ordinalToDate(span.end.n);
  if (to === null || to < (provider.limits.earliest ?? new Date(0)))
    return null;
  const earliest = provider.limits.earliest ?? new Date(0);
  const start = from && from > earliest ? from : earliest;
  // A bucket wholly in the future has nothing in it by definition, and asking
  // costs a request per empty year when someone scrolls forward.
  if (start > now) return null;
  return [start, to];
}

export async function loadExternalSource(
  source: ExternalSource,
  target: ExternalLoadTarget,
  signal: AbortSignal,
): Promise<ExternalLoadResult> {
  const provider = getProvider(source.providerKey);
  const title = provider?.title ?? source.providerKey;

  const status = (
    state: ExternalSourceState,
    extra: Partial<ExternalSourceStatus> = {},
  ): ExternalSourceStatus => ({
    collectionId: source.collectionId,
    providerKey: source.providerKey,
    title,
    state,
    ...extra,
  });

  if (!provider) {
    return {
      records: [],
      status: status("unsupported", {
        message: `This build does not know how to read ${source.providerKey}.`,
      }),
    };
  }

  if (!source.connectionId) {
    return {
      records: [],
      status: status("auth", {
        message: `Connect ${provider.title} to see anything here.`,
      }),
    };
  }

  const range = windowFor(provider, target.secondsRange);
  if (!range) {
    return { records: [], status: status("empty") };
  }

  const buildOptions = {
    collectionId: source.collectionId,
    providerKey: source.providerKey,
    userId: target.userId,
    secondsRange: target.secondsRange,
  };

  try {
    const accessToken = await getAccessToken(source.connectionId);
    const spanDays = (range[1].getTime() - range[0].getTime()) / MS_PER_DAY;

    if (spanDays <= provider.limits.detailSpanDays) {
      const ctx: ProviderFetchContext = {
        accessToken,
        config: source.config,
        from: range[0],
        to: range[1],
        limit: provider.limits.maxItems,
        signal,
      };
      const items = await provider.fetchItems(ctx);
      const records = await adaptItems(
        items,
        source.config,
        provider.itemUrl.bind(provider),
        buildOptions,
      );
      return {
        records,
        status: status("ok", {
          truncated: items.length >= provider.limits.maxItems,
        }),
      };
    }

    const now = new Date();
    const buckets = summaryBuckets(target.interval, target.endInterval);
    const results = await Promise.all(
      buckets.map(async (bucket) => {
        const bucketRange = bucketWindow(bucket, provider, now);
        if (!bucketRange) return [];
        const summary = await provider.fetchSummary({
          accessToken,
          config: source.config,
          from: bucketRange[0],
          to: bucketRange[1],
          limit: 1,
          signal,
        });
        return adaptSummary(summary, bucket, provider.title, buildOptions);
      }),
    );

    return { records: results.flat(), status: status("ok") };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError")
      throw error;
    if (error instanceof ExternalAuthError) {
      return {
        records: [],
        status: status("auth", {
          message: `${provider.title} access expired. Reconnect to see it again.`,
        }),
      };
    }
    return {
      records: [],
      status: status("error", {
        message:
          error instanceof Error
            ? error.message
            : `Could not read ${provider.title}.`,
      }),
    };
  }
}
